import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

function getApiErrorMessage(json: ApiErrorResponse | undefined, fallback: string): string {
  if (!json) return fallback;
  if (Array.isArray(json.message)) {
    return json.message.map(String).filter(Boolean).join(" ");
  }
  if (typeof json.message === "string" && json.message.trim()) {
    return json.message;
  }
  if (typeof json.error === "string" && json.error.trim()) {
    return json.error;
  }
  return fallback;
}

export interface FollowUpNextLeadNumberResponse {
  leadNo: string;
}

export async function getFollowUpNextLeadNumber(): Promise<FollowUpNextLeadNumberResponse> {
  const res = await apiFetch(`${API_BASE}/follow-up/discussion/next-lead-number`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpNextLeadNumberResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to get next lead number")
    );
  }

  return (json as ApiSuccessResponse<FollowUpNextLeadNumberResponse>).data;
}

export interface CreateFollowUpDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
}

export interface CreateFollowUpDiscussionResponse {
  leadId: string;
  leadNo: string;
}

export async function createFollowUpDiscussion(
  payload: CreateFollowUpDiscussionPayload
): Promise<CreateFollowUpDiscussionResponse> {
  const res = await apiFetch(`${API_BASE}/follow-up/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<CreateFollowUpDiscussionResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to create follow-up discussion")
    );
  }

  return (json as ApiSuccessResponse<CreateFollowUpDiscussionResponse>).data;
}

export interface UpdateFollowUpPreliminaryPayload {
  markName?: string;
  contactName?: string;
  phone?: string;
  email?: string;
  callTime?: string;
}

export async function updateFollowUpPreliminary(
  leadId: string,
  payload: UpdateFollowUpPreliminaryPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(`${API_BASE}/follow-up/discussion/${leadId}/preliminary`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to update preliminary discussion")
    );
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

export interface FollowUpDiscussionDetails {
  leadId: string;
  leadNo: string;
  preliminary: {
    markName: string | null;
    contactName: string;
    phone: string | null;
    email: string | null;
    callTime: string | null;
  };
  applicationDetails: {
    additionalNote: string | null;
    documents: Array<{ fileKey: string; fileName: string; fileUrl: string | null }>;
  };
  advanceAmount: number;
  applications: Array<{
    id: string;
    tmNo: string;
    dateOfFiling: string | null;
    mark: string | null;
    typeOfMark: string | null;
    classNumber: number | null;
    goodsServices: string | null;
    userDateType: string | null;
    userDate: string | null;
    status: string | null;
    sortOrder: number;
  }>;
}

export async function getFollowUpDiscussionDetails(
  leadId: string
): Promise<FollowUpDiscussionDetails> {
  const res = await apiFetch(`${API_BASE}/follow-up/discussion/${leadId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpDiscussionDetails>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load discussion details")
    );
  }

  return (json as ApiSuccessResponse<FollowUpDiscussionDetails>).data;
}

export interface FollowUpApplicationDetailsDocumentInput {
  fileKey: string;
  fileName: string;
}

export interface SubmitFollowUpApplicationDetailsPayload {
  documents: FollowUpApplicationDetailsDocumentInput[];
  additionalNote?: string;
}

export async function submitFollowUpApplicationDetails(
  leadId: string,
  payload: SubmitFollowUpApplicationDetailsPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/application-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to submit application details"
      )
    );
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

export interface FollowUpUploadPresignedUrl {
  key: string;
  bucket: string;
  url: string;
}

export async function generateFollowUpUploadPresignedUrls(
  leadId: string,
  files: Array<{ filename: string; mimeType: string }>
): Promise<FollowUpUploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      files,
      type: "private",
      folder: `follow-up/discussion/${leadId}`,
    }),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpUploadPresignedUrl[]>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to generate upload URLs")
    );
  }

  return (json as ApiSuccessResponse<FollowUpUploadPresignedUrl[]>).data;
}

// --- Step 3: Invoice of Advance Amount to Client ---

export interface FollowUpAdvanceAmountPreviewResponse {
  message: string;
  waLink: string;
}

export async function getFollowUpAdvanceAmountPreview(
  leadId: string,
  advanceAmount?: number
): Promise<FollowUpAdvanceAmountPreviewResponse> {
  const qs =
    advanceAmount !== undefined && Number.isFinite(advanceAmount)
      ? `?advanceAmount=${encodeURIComponent(String(advanceAmount))}`
      : "";
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/advance-amount/preview${qs}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpAdvanceAmountPreviewResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load advance amount preview"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpAdvanceAmountPreviewResponse>).data;
}

export interface FollowUpInvoiceAdvancePayload {
  advanceAmount?: number;
  sendWhatsApp?: boolean;
}

export async function submitFollowUpInvoiceAdvanceAmount(
  leadId: string,
  payload: FollowUpInvoiceAdvancePayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/advance-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to submit invoice advance amount"
      )
    );
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Workflow progress (used to detect already-sent steps) ---

export interface FollowUpProgressStep {
  slug: string;
  label: string;
  stage: string;
  stepType: string;
  parentStep?: string;
  isCompleted: boolean;
  isSkipped: boolean;
}

export interface FollowUpProgressResponse {
  currentStep: string | null;
  currentSection: string | null;
  maxStepReached: string | null;
  steps: FollowUpProgressStep[];
}

export async function getFollowUpProgress(
  leadId: string
): Promise<FollowUpProgressResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/progress`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpProgressResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load workflow progress")
    );
  }

  return (json as ApiSuccessResponse<FollowUpProgressResponse>).data;
}

/** Backend FollowUpStep enum values used for workflow progress lookup. */
export const FollowUpWorkflowStep = {
  FU_S1_03_InvoiceOfAdvanceAmountToClient:
    "FU_S1_03_INVOICE_OF_ADVANCE_AMOUNT_TO_CLIENT",
  FU_S1_04_AdvanceAmountFromClientForFollowUp:
    "FU_S1_04_ADVANCE_AMOUNT_FROM_CLIENT_FOR_FOLLOW_UP",
  FU_S1_05_AnalysisReportOnStatusOfApplication:
    "FU_S1_05_ANALYSIS_REPORT_ON_STATUS_OF_APPLICATION",
  FU_S1_06_LegalAdviceForFollowUp: "FU_S1_06_LEGAL_ADVICE_FOR_FOLLOW_UP",
  FU_S1_07_InvoiceToClientForFollowUp:
    "FU_S1_07_INVOICE_TO_CLIENT_FOR_FOLLOW_UP",
  FU_S1_08_PaymentFromClientForFollowUp:
    "FU_S1_08_PAYMENT_FROM_CLIENT_FOR_FOLLOW_UP",
} as const;

// --- Step 4: Advance Amount from Client ---

export interface FollowUpAdvanceAmountFormData {
  advanceAmount: number;
  collectedAdvanceAmount: number;
}

export async function getFollowUpAdvanceAmountFormData(
  leadId: string
): Promise<FollowUpAdvanceAmountFormData> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/advance-amount/received`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpAdvanceAmountFormData>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load advance amount details"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpAdvanceAmountFormData>).data;
}

export interface RecordFollowUpAdvanceAmountReceivedPayload {
  collectedAdvanceAmount: number;
}

export async function recordFollowUpAdvanceAmountReceived(
  leadId: string,
  payload: RecordFollowUpAdvanceAmountReceivedPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/advance-amount/received`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to record advance amount from client"
      )
    );
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 5: Analysis Report on the status of application ---

export const FOLLOW_UP_ADDRESSEE_DESIGNATIONS = [
  "The Proprietor",
  "The Managing Partner",
  "The Designated Partner",
  "The Managing Director",
  "The Managing Trustee",
  "The Chairman",
  "NAME",
] as const;
export type FollowUpAddresseeDesignation =
  (typeof FOLLOW_UP_ADDRESSEE_DESIGNATIONS)[number];

export const FOLLOW_UP_APPLICANT_TYPES = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
] as const;

export const FOLLOW_UP_FORM_OF_ADDRESS_OPTIONS = ["Sir", "Madam"] as const;

export const FOLLOW_UP_TYPE_OF_MARK_OPTIONS = [
  "Word",
  "Logo",
  "Label",
  "Slogan",
] as const;

export const FOLLOW_UP_CLASS_NUMBERS = Array.from({ length: 44 }, (_, i) => i + 1);

export const FOLLOW_UP_PLACE_OPTIONS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
] as const;

export const FOLLOW_UP_APPLICATION_STATUS_VALUES = [
  "TM No. Allotted",
  "Formalities Check Pass",
  "Formalities Check Fail",
  "Marked for Exam",
  "Accepted",
  "Objected",
  "Examination Report - Accepted after Objection",
  "Examination Report - Hearing",
  "Advertised",
  "Advertised in Journal - Opposed",
  "Opposition Hearing",
  "Advertised in Journal - Not Opposed",
  "Registered",
  "Abandoned",
  "Refused",
] as const;

export const FOLLOW_UP_USER_DATE_TYPES = {
  ProposedToBeUsed: "PROPOSED_TO_BE_USED",
  SpecificDate: "SPECIFIC_DATE",
} as const;
export type FollowUpUserDateType =
  (typeof FOLLOW_UP_USER_DATE_TYPES)[keyof typeof FOLLOW_UP_USER_DATE_TYPES];

export interface FollowUpAnalysisReportPreviewApplication {
  tmNo: string;
  dateOfFiling: string;
  mark: string;
  typeOfMark: string;
  class: number;
  goodsServices: string;
  imageOfMarkFileKey: string | null;
  imageOfMarkUrl: string | null;
  userDateDisplay: string;
  status: string | null;
}

export interface FollowUpAnalysisReportPreview {
  addressee: string;
  formOfAddress: string;
  applicant: string;
  applicantAddress: string;
  applicantType: string;
  applications: FollowUpAnalysisReportPreviewApplication[];
  textOfReport: string;
  dateOfReport: string;
  reportNo: string;
  noOfApplications: number;
  applsLabel: string;
  reportPdfKey: string | null;
  reportPdfUrl: string | null;
  reportPdfFilename: string | null;
  isReportPdfStale: boolean;
}

export async function getFollowUpAnalysisReportPreview(
  leadId: string
): Promise<FollowUpAnalysisReportPreview> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/analysis-report/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpAnalysisReportPreview>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load analysis report preview"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpAnalysisReportPreview>).data;
}

export interface FollowUpAnalysisReportApplicationInput {
  id?: string;
  tmNo: string;
  dateOfFiling?: string | null;
  mark?: string | null;
  typeOfMark?: string | null;
  classNumber?: number | null;
  goodsServices?: string | null;
  imageOfMarkFileKey?: string | null;
  imageOfMark?: string | null;
  userDateType?: FollowUpUserDateType | null;
  userDate?: string | null;
  status?: string | null;
  sortOrder?: number;
}

export interface UpsertFollowUpAnalysisReportPayload {
  addresseeDesignation?: FollowUpAddresseeDesignation;
  addresseeCustomName?: string | null;
  firmName?: string | null;
  place?: string | null;
  districtOfApplicant?: string | null;
  stateOfApplicant?: string | null;
  countryOfApplicant?: string | null;
  formOfAddress?: string | null;
  applicant?: string | null;
  applicantAddress?: string | null;
  applicantType?: string | null;
  signatoryName?: string | null;
  signatoryDesignation?: string | null;
  textOfReport?: string | null;
  dateOfReport?: string | null;
  applications?: FollowUpAnalysisReportApplicationInput[];
}

export async function upsertFollowUpAnalysisReport(
  leadId: string,
  payload: UpsertFollowUpAnalysisReportPayload
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/analysis-report`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as ApiSuccessResponse<undefined> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to save analysis report"
      )
    );
  }
}

export interface FollowUpAnalysisReportPdfResponse {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export async function generateFollowUpAnalysisReportPdf(
  leadId: string
): Promise<FollowUpAnalysisReportPdfResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/analysis-report/generate-pdf`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpAnalysisReportPdfResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to generate analysis report PDF"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpAnalysisReportPdfResponse>).data;
}

export interface SkipFollowUpStepResponse {
  step: string;
  status: string;
}

/** Marks a follow-up workflow step as skipped (Progress button). */
export async function skipFollowUpStep(
  leadId: string,
  step: string
): Promise<SkipFollowUpStepResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/discussion/${leadId}/workflow/skip-step`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ step }),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SkipFollowUpStepResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to skip step")
    );
  }

  return (json as ApiSuccessResponse<SkipFollowUpStepResponse>).data;
}

// --- Step 6: Legal Advice for Follow-up ---

export interface FollowUpLegalAdvice {
  id: string;
  leadId: string;
  clientGoingAhead: boolean;
  conversationDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function getFollowUpLegalAdvice(
  leadId: string
): Promise<FollowUpLegalAdvice | null> {
  const res = await apiFetch(`${API_BASE}/follow-up/legal-advice/${leadId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpLegalAdvice | null>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load legal advice")
    );
  }

  return (json as ApiSuccessResponse<FollowUpLegalAdvice | null>).data;
}

export interface SubmitFollowUpLegalAdvicePayload {
  clientGoingAhead: boolean;
  conversationDate?: string;
  advanceAmount?: number;
  sendWhatsApp?: boolean;
}

export interface SubmitFollowUpLegalAdviceResponse {
  leadId: string;
  clientGoingAhead: boolean;
  invoiceSent?: boolean;
}

export async function submitFollowUpLegalAdvice(
  leadId: string,
  payload: SubmitFollowUpLegalAdvicePayload
): Promise<SubmitFollowUpLegalAdviceResponse> {
  const res = await apiFetch(`${API_BASE}/follow-up/legal-advice/${leadId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<SubmitFollowUpLegalAdviceResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to submit legal advice")
    );
  }

  return (json as ApiSuccessResponse<SubmitFollowUpLegalAdviceResponse>).data;
}

// --- Step 7: Invoice to Client for Follow-up ---

export interface FollowUpInvoiceToClientPreviewApplication {
  applicationNumber: string;
  dateOfFiling: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  status: string | null;
}

export interface FollowUpInvoiceToClientPreview {
  addressee: string;
  formOfAddress: string;
  mark: string;
  selectedClass: string;
  selectedTypeOfMark: string;
  noOfApplications: number;
  attorneyChangeChargesPerApp: number;
  followUpChargesPerApp: number;
  totalAttorneyChangeCharges: number;
  totalFollowUpCharges: number;
  discountProfFeeFollowUp: number;
  totalDiscount: number;
  collectedAdvance: number;
  totalCharges: number;
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  applications: FollowUpInvoiceToClientPreviewApplication[];
  isInvoiceStale: boolean;
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
}

export async function getFollowUpInvoiceToClientPreview(
  leadId: string
): Promise<FollowUpInvoiceToClientPreview> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpInvoiceToClientPreview>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load invoice preview"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpInvoiceToClientPreview>).data;
}

export interface SaveFollowUpInvoiceToClientPayload {
  invoiceDate?: string;
  discountProfFeeFollowUp?: number;
  includeInvoiceNo?: boolean;
}

export interface SaveFollowUpInvoiceToClientResponse {
  leadId: string;
  invoiceNo: string;
  invoiceDate: string;
  includeInvoiceNo: boolean;
}

export async function saveFollowUpInvoiceToClient(
  leadId: string,
  payload: SaveFollowUpInvoiceToClientPayload
): Promise<SaveFollowUpInvoiceToClientResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/save`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SaveFollowUpInvoiceToClientResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to save invoice")
    );
  }

  return (json as ApiSuccessResponse<SaveFollowUpInvoiceToClientResponse>).data;
}

export interface FollowUpInvoiceToClientPdfResponse {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

export async function generateFollowUpInvoiceToClientPdf(
  leadId: string
): Promise<FollowUpInvoiceToClientPdfResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/generate`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpInvoiceToClientPdfResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to generate invoice PDF"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpInvoiceToClientPdfResponse>).data;
}

export interface SendFollowUpInvoiceToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export interface SendFollowUpInvoiceToClientResponse {
  whatsAppLink?: string;
  emailSent?: boolean;
  leadId?: string;
}

export async function sendFollowUpInvoiceToClient(
  leadId: string,
  payload: SendFollowUpInvoiceToClientPayload = {}
): Promise<SendFollowUpInvoiceToClientResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SendFollowUpInvoiceToClientResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to send invoice to client"
      )
    );
  }

  return (json as ApiSuccessResponse<SendFollowUpInvoiceToClientResponse>).data;
}

export interface FollowUpInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface FollowUpInvoicePaymentMessagePreview {
  message: string;
  waLink: string;
  emailPreview: FollowUpInvoiceEmailPreview;
}

export async function getFollowUpInvoicePaymentMessagePreview(
  leadId: string
): Promise<FollowUpInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/payment-message-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpInvoicePaymentMessagePreview>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load payment message preview"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpInvoicePaymentMessagePreview>).data;
}

export async function sendFollowUpInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/to-client/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to send payment message"
      )
    );
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Step 8: Payment from Client for Follow-up ---

export interface RecordFollowUpPaymentFromClientPayload {
  collectedAmount: number;
}

export interface RecordFollowUpPaymentFromClientResponse {
  invoiceAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
}

export async function recordFollowUpPaymentFromClient(
  leadId: string,
  payload: RecordFollowUpPaymentFromClientPayload
): Promise<RecordFollowUpPaymentFromClientResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/invoice/${leadId}/payment`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<RecordFollowUpPaymentFromClientResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to record payment from client"
      )
    );
  }

  return (json as ApiSuccessResponse<RecordFollowUpPaymentFromClientResponse>).data;
}
